<script setup lang="ts">
import SidebarResizeHandle from "../ui/SidebarResizeHandle.vue";
import { useSidebarWidthsStore } from "../../stores/useSidebarWidthsStore";
const sidebarWidths = useSidebarWidthsStore();
import { useEditorSessionStore } from "../../stores/useEditorSessionStore";
defineProps<{ title: string; bodyClass: string }>();
const session = useEditorSessionStore();
</script>
<template>
  <aside
    class="zw-context"
    :style="{ '--sidebar-width': `${sidebarWidths.widths.right}rem` }"
    :class="{
      'zw-context-collapsed':
        session.contextPanelCollapsed && !session.mobileShell,
      'zw-context--overlay': session.mobileShell,
    }"
    :aria-label="title"
  >
    <button
      v-if="!session.mobileShell"
      type="button"
      class="zw-context-expand"
      :title="`Show ${title.toLowerCase()}`"
      :aria-label="`Show ${title.toLowerCase()}`"
      :tabindex="session.contextPanelCollapsed ? 0 : -1"
      :aria-hidden="!session.contextPanelCollapsed"
      @click="session.contextPanelCollapsed = false"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        class="size-4"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"
        />
      </svg>
    </button>

    <div
      class="zw-context-panel"
      :inert="session.contextPanelCollapsed && !session.mobileShell"
      :aria-hidden="session.contextPanelCollapsed && !session.mobileShell"
    >
      <header class="zw-context-header">
        <h2>
          {{ title }}
        </h2>
        <slot name="actions" />
        <button
          v-if="!session.mobileShell"
          type="button"
          class="zw-context-hide"
          :title="`Hide ${title.toLowerCase()}`"
          :aria-label="`Hide ${title.toLowerCase()}`"
          @click="session.contextPanelCollapsed = true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            class="size-3.5"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m9 6 6 6-6 6"
            />
          </svg>
        </button>
      </header>

      <div class="zw-context-body zw-scroll" :class="bodyClass">
        <slot />
      </div>
    </div>
    <SidebarResizeHandle
      v-if="!session.mobileShell && !session.contextPanelCollapsed"
      side="right"
    />
  </aside>
</template>
<style scoped>
.zw-context {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: clamp(
    14.5rem,
    var(--sidebar-width, 14.5rem),
    min(29rem, calc(50vw - 12rem))
  );
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border-left: 1px solid var(--line);
  background: color-mix(in oklch, var(--bg) 92%, var(--bg-raised));
  font-family: var(--font-ui);
}

.zw-context--overlay {
  width: 100%;
  border-left: 0;
  background: var(--bg);
}

.zw-context--overlay .zw-context-panel {
  width: 100%;
}

.zw-context-collapsed {
  width: 2.5rem;
}

.zw-context-expand,
.zw-context-hide {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    color var(--duration-fast) var(--ease-out-quart),
    opacity var(--duration-fast) var(--ease-out-quart),
    background-color var(--duration-fast) var(--ease-out-quart);
}

.zw-context-expand {
  position: absolute;
  top: var(--space-md);
  left: 50%;
  z-index: 1;
  width: 2rem;
  height: 2rem;
  transform: translateX(-50%);
  opacity: 0;
  pointer-events: none;
}

.zw-context-collapsed .zw-context-expand {
  opacity: 1;
  pointer-events: auto;
}

.zw-context-hide {
  width: 1.6rem;
  height: 1.6rem;
  color: var(--text-muted);
}

.zw-context-expand:hover,
.zw-context-hide:hover {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.zw-context-expand:focus-visible,
.zw-context-hide:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.zw-context-panel {
  display: flex;
  flex-direction: column;
  width: clamp(
    14.5rem,
    var(--sidebar-width, 14.5rem),
    min(29rem, calc(50vw - 12rem))
  );
  min-height: 0;
  flex: 1;
  flex-shrink: 0;
  opacity: 1;
  transition: opacity var(--duration-fast) var(--ease-out-quart);
}

.zw-context-collapsed .zw-context-panel {
  opacity: 0;
  pointer-events: none;
}

.zw-context-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  flex-shrink: 0;
  padding: var(--space-md) var(--space-md) var(--space-sm);
}

.zw-context-header h2 {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.zw-context-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  /* Match `.zw-editor-dock-inset` so occurrences clear the status pill. */
  padding: 0 var(--space-sm) calc(var(--space-md) + 2.35rem);
}
</style>
